import { cloneElement, useEffect, useMemo, useRef } from 'react'
import SwitcherSheet from './SwitcherSheet'
import ParentRef from '../common/components/ParentRef'
import { MSP_URL } from '../common/constants/GlobalConstants'
import { stateRef, useSwitcherStoreState, open, setTsg, selectTsg, close } from './store'
import type { MutableRefObject, ReactElement, VFC } from 'react'
import type { AuthState } from '@sparky/framework'

export type TenantAppSwitcherProps = {
  fallback: ReactElement
  auth: AuthState
  theme: string | undefined
  pathPrefix: string
}

const TenantAppSwitcher: VFC<TenantAppSwitcherProps> = ({ fallback, auth, theme, pathPrefix }) => {
  const {
    isOpen,
    contextTsg,
    tsg,
    tsgs,
    tsgAppInstances,
    isHub,
    isSingleTenantOnly,
  } = useSwitcherStoreState(auth)
  const singleTenantOnly = useMemo(() => { // no tenant switcher if single tenant or in msp mode
    if (isSingleTenantOnly) {
      return true
    }
    if (pathPrefix) {
      return pathPrefix === MSP_URL // || pathPrefix === ASC_URL
    }
    return false
  }, [isSingleTenantOnly, pathPrefix])
  // const isLoading = !instances || !apps?.length
  const navRef = useRef<HTMLElement>(null) as MutableRefObject<HTMLElement>

  useEffect(() => {
    if (!isOpen) { // clear tsg when closed
      setTsg(undefined)
    }
    else if (!stateRef.current.tsg && contextTsg) {
      // reset tsg to context tsg when opened or tsg switched
      setTsg(contextTsg)
    }
  }, [isOpen, contextTsg])

  useEffect(() => { // fix tooltip z-index issue
    if (navRef.current) {
      if (isOpen) {
        navRef.current.style.zIndex = '40'
      }
      else {
        navRef.current.style.zIndex = ''
      }
    }
  }, [isOpen, navRef])

  const sheet = useMemo(() => (
    <SwitcherSheet
      isOpen={isOpen}
      isHub={isHub}
      isSingleTenantOnly={singleTenantOnly}
      close={close}
      leftRef={navRef}
      tsgAppInstances={tsgAppInstances}
      contextTsg={contextTsg}
      selectTsg={selectTsg}
      tsg={tsg || contextTsg}
      tsgs={tsgs}
      theme={theme}
      stateRef={stateRef}
    />
  ), [isOpen, isHub, singleTenantOnly, theme, tsgAppInstances, contextTsg, tsg, tsgs])

  const switcherButton = useMemo(() => {
    if (fallback) {
      return cloneElement(fallback, {
        onClick: open
      })
    }
    return null
  }, [fallback])

  return <>
    <ParentRef parentRef={navRef} closest='.sparky-left-nav' />
    {switcherButton}
    {sheet}
  </>
}

export default TenantAppSwitcher
